import { useQuery } from '@tanstack/react-query';
import { useVirtualizer } from '@tanstack/react-virtual';
import { Loader2, Search } from 'lucide-react';
import { debounce } from 'radash';
import { useCallback, useEffect, useRef, useState } from 'react';

import { Input } from '@/components/ui/input';

import { NutritionForm } from './ingredient-nutrition';

// USDA Food Data Central API types
export interface USDAFood {
  fdcId: number;
  description: string;
  dataType: string;
  brandOwner?: string;
  ingredients?: string;
  foodNutrients?: {
    nutrientId: number;
    nutrientName: string;
    value: number;
    unitName: string;
  }[];
}

interface USDASearchResponse {
  foods: USDAFood[];
  totalHits: number;
  currentPage: number;
  totalPages: number;
}

/**
 * Converts nutrients from FCDA food to expected format
 *
 * This function maps the nutrient IDs from the USDA API to
 * the NutritionForm structure used in the application.
 *
 * Based on USDA nutrient ids from https://fdc.nal.usda.gov/download-datasets as on 2025-06-19
 *
 * @param nutrients - Array of food nutrients from USDA API
 * @return NutritionForm - Formatted nutrition data
 */
const convertNutrients = (
  nutrients: USDAFood['foodNutrients'] = [],
): NutritionForm => {
  const nutrientMap: Record<number, number | undefined> = Object.fromEntries(
    nutrients.map((n) => [n.nutrientId, n.value]),
  );

  const getNutrientValue = (id: number): number =>
    Math.round(nutrientMap[id] ?? 0);

  return {
    fat: getNutrientValue(1004),
    fat_saturated: getNutrientValue(1258),
    fat_monounsaturates: getNutrientValue(1292),
    fat_polyunsaturates: getNutrientValue(1293),
    carbohydrate: getNutrientValue(1005),
    carbohydrate_sugars: getNutrientValue(2000),
    carbohydrate_polyols: getNutrientValue(1086),
    carbohydrate_starch: getNutrientValue(1009),
    fibre: getNutrientValue(1079),
    protein: getNutrientValue(1003),
    salt: Math.round(getNutrientValue(1093) * 0.001),
  };
};

// USDA API key - in production this should be in environment variables
const USDA_API_KEY = 'gLIOwu0sxJxWJRw6ZjOGToTafbGb2WrViEEOLLmU'; // Replace with your actual API key

// Function to search USDA Food Data Central
const searchUSDAFoods = async (query: string): Promise<USDASearchResponse> => {
  if (!query.trim()) {
    return { foods: [], totalHits: 0, currentPage: 1, totalPages: 0 };
  }

  const response = await fetch(
    `https://api.nal.usda.gov/fdc/v1/foods/search?query=${encodeURIComponent(query)}&pageSize=50&api_key=${USDA_API_KEY}`,
  );

  if (!response.ok) {
    throw new Error('Failed to search USDA database');
  }

  return response.json() as Promise<USDASearchResponse>;
};

// Custom hook for USDA search functionality
function useUSDASearch() {
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');

  // Create debounced function using radash
  const debouncedSearch = useCallback(
    debounce({ delay: 500 }, (query: string) => {
      setDebouncedQuery(query);
    }),
    [setDebouncedQuery],
  );

  // Auto-search when searchQuery changes
  useEffect(() => {
    if (searchQuery.length > 2) {
      debouncedSearch(searchQuery);
    } else {
      setDebouncedQuery('');
    }
  }, [searchQuery, debouncedSearch]);

  // React Query for USDA search
  const {
    data: searchResults,
    isLoading,
    error,
    isError,
  } = useQuery({
    queryKey: ['usda-food-search', debouncedQuery],
    queryFn: () => searchUSDAFoods(debouncedQuery),
    enabled: debouncedQuery.length > 2, // Only search if query is longer than 2 characters
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  return {
    searchQuery,
    setSearchQuery,
    searchResults,
    isLoading,
    error,
    isError,
    debouncedQuery,
  };
}

interface IngredientNutritionSearchPresentationProps {
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  searchResults: USDASearchResponse | undefined;
  isLoading: boolean;
  error: Error | null;
  isError: boolean;
  debouncedQuery: string;
  onFoodSelect?: (food: NutritionForm) => void;
}

function IngredientNutritionSearchPresentation({
  searchQuery,
  setSearchQuery,
  searchResults,
  isLoading,
  error,
  isError,
  debouncedQuery,
  onFoodSelect,
}: IngredientNutritionSearchPresentationProps) {
  // Ref for the virtual list container
  const parentRef = useRef<HTMLDivElement>(null);

  // Virtualization setup
  const foods = searchResults?.foods ?? [];
  const virtualizer = useVirtualizer({
    count: foods.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 60, // Estimated height of each item
    overscan: 5, // Render 5 extra items outside viewport
  });

  const handleFoodSelect = (food: USDAFood) => {
    if (onFoodSelect) {
      onFoodSelect(convertNutrients(food.foodNutrients));
    }
  };

  const handleFoodKeyDown = (e: React.KeyboardEvent, food: USDAFood) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleFoodSelect(food);
    }
  };

  return (
    <div className="space-y-4">
      {/* Search Input */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
        {isLoading && (
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
            <Loader2 className="text-gray-400 h-4 w-4 animate-spin" />
          </div>
        )}
        <Input
          type="text"
          placeholder="Search USDA food database..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10 pr-10"
        />
      </div>

      {/* Search Results */}
      {isError && (
        <div className="text-red-600 text-sm">
          Error searching USDA database: {error?.message}
        </div>
      )}

      {searchResults && searchResults.foods.length > 0 && (
        <div className="space-y-2">
          <div
            ref={parentRef}
            className="h-80 overflow-auto border border-gray-200 rounded-md"
          >
            <div
              style={{
                height: `${virtualizer.getTotalSize()}px`,
                width: '100%',
                position: 'relative',
              }}
            >
              {virtualizer.getVirtualItems().map((virtualItem) => {
                const food = foods[virtualItem.index];
                return (
                  <button
                    key={food.fdcId}
                    type="button"
                    className="absolute top-0 left-0 w-full text-left p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 focus:outline-none focus:bg-gray-50"
                    style={{
                      height: `${virtualItem.size}px`,
                      transform: `translateY(${virtualItem.start}px)`,
                    }}
                    onClick={() => handleFoodSelect(food)}
                    onKeyDown={(e) => handleFoodKeyDown(e, food)}
                  >
                    <div className="font-medium text-sm truncate">
                      {food.description}
                    </div>

                    {food.brandOwner && (
                      <div className="text-xs text-gray-600 mt-1 truncate">
                        Brand: {food.brandOwner}
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {searchResults && searchResults.foods.length === 0 && debouncedQuery && (
        <div className="text-gray-600 text-sm text-center py-4">
          No foods found for &ldquo;{debouncedQuery}&rdquo;
        </div>
      )}

      {debouncedQuery.length > 0 && debouncedQuery.length <= 2 && (
        <div className="text-gray-500 text-sm">
          Please enter at least 3 characters to search
        </div>
      )}
    </div>
  );
}

interface IngredientNutritionSearchProps {
  onFoodSelect?: (food: NutritionForm) => void;
}

export function IngredientNutritionSearch({
  onFoodSelect,
}: IngredientNutritionSearchProps) {
  const {
    searchQuery,
    setSearchQuery,
    searchResults,
    isLoading,
    error,
    isError,
    debouncedQuery,
  } = useUSDASearch();

  return (
    <IngredientNutritionSearchPresentation
      searchQuery={searchQuery}
      setSearchQuery={setSearchQuery}
      searchResults={searchResults}
      isLoading={isLoading}
      error={error}
      isError={isError}
      debouncedQuery={debouncedQuery}
      onFoodSelect={onFoodSelect}
    />
  );
}
